/* eslint-disable no-nested-ternary */
import { FC, PropsWithChildren } from 'react';
import { Box, Card, Svg, Typography } from '@altermaind/web/ui';
import { StepCircle, StepIndicator } from './styles';
import { VerticalStepperProps, VerticalStepWrapperProps } from './types';

const STEP_PAD_LENGTH = 10;

// Component that draws vertical line and circle
const StepWrapper: FC<PropsWithChildren<VerticalStepWrapperProps>> = ({
  index,
  isFirst,
  isLast,
  children,
  size,
  isCompleted,
  primaryStyle,
  iconName,
  isDisabled,
  hasOnlyOneStep,
}) => (
  <Box display='flex' flexDirection='row' alignItems='stretch'>
    <StepIndicator
      isFirst={isFirst}
      isLast={isLast}
      circleSize={size}
      lineColor={primaryStyle ? 'primary' : 'primary/text'}
      hasOnlyOneStep={hasOnlyOneStep}
    >
      {isCompleted ? (
        <StepCircle size={isCompleted ? 'm' : size} bg='primary/text' color='primary'>
          <Svg name='check' fill='primary' size='s' />
        </StepCircle>
      ) : (
        <StepCircle
          size={isDisabled ? 'm' : size}
          bg={primaryStyle ? 'primary' : 'primary/text'}
          color={primaryStyle ? 'white' : 'primary'}
        >
          {iconName ? (
            <Svg name={iconName} fill='white' stroke='white' size='xs' />
          ) : index < STEP_PAD_LENGTH ? (
            `0${index}`
          ) : (
            index
          )}
        </StepCircle>
      )}
    </StepIndicator>
    <Box flex='1' marginLeft='m'>
      {children}
    </Box>
  </Box>
);

/**
 * VerticalStepper is a functional component that renders a vertical stepper UI.
 * It displays a series of steps with optional titles and descriptions, styled with a consistent layout.
 *
 * @param {string} [title] - An optional title displayed at the top of the stepper.
 * @param {Array} steps - An array of step objects, where each step contains:
 *   - `title` (string): The title of the step.
 *   - `description` (string): A brief description of the step.
 * @param {string} [stepIndicatorSize='l'] - The size of the step indicator, defaulting to 'l'.
 * @param {boolean} [noBorder=false] - If true, the steps will be rendered without borders.
 * @param {boolean} [primaryStyle=false] - If true, applies primary styling to the stepper.
 *
 * @returns {JSX.Element} A vertical stepper component with styled steps.
 *
 * @example
 * const steps = [
 *   { title: 'Step 1', description: 'This is the first step.' },
 *   { title: 'Step 2', description: 'This is the second step.' },
 *   { title: 'Step 3', description: 'This is the third step.' },
 * ];
 *
 * <VerticalStepper
 *   title="My Stepper"
 *   steps={steps}
 *   stepIndicatorSize="l"
 * />
 */
export const VerticalStepper: FC<VerticalStepperProps> = ({
  title,
  steps,
  stepIndicatorSize = 'l',
  noBorder = false,
  primaryStyle = false,
}) => {
  const styleBorder = {
    borderWidth: noBorder ? 0 : 1,
    borderColor: noBorder ? 'transparent' : 'white',
    borderStyle: noBorder ? 'none' : 'solid',
  };

  return (
    <Box display='flex' flexDirection='column' style={{ gap: 18 }}>
      {title && (
        <Box display='flex' alignItems='center'>
          <Box width={stepIndicatorSize} />
          <Typography
            variant='h3'
            fontWeight={200}
            color='primary/text'
            mb='xxs'
            ml='m'
            style={{ letterSpacing: 1 }}
          >
            {title}
          </Typography>
        </Box>
      )}
      {steps.map((step, idx) => (
        <StepWrapper
          // eslint-disable-next-line react/no-array-index-key
          key={`StepWrapper_${idx}`}
          isFirst={idx === 0}
          isLast={idx === steps.length - 1}
          index={idx + 1}
          size={stepIndicatorSize}
          isCompleted={step.isCompleted}
          primaryStyle={primaryStyle}
          iconName={step.iconName}
          isDisabled={step.isDisabled}
          hasOnlyOneStep={steps.length === 1}
        >
          <Card
            padding={steps.length === 1 ? 'none' : noBorder ? 'xs' : 'm'}
            bg='transparent'
            style={styleBorder}
            borderRadius={noBorder ? 'none' : 'l'}
          >
            <Typography
              variant='h3'
              color={step.isCompleted || step.isDisabled ? 'grey/light/background' : 'primary/text'}
              marginBottom='xxs'
            >
              {step.title}
            </Typography>
            {!step.isDisabled && (step.isCompleted === false || step.isCompleted === undefined) && (
              <Typography variant='p1/light' color='grey/light'>
                {step.description}
              </Typography>
            )}
          </Card>
        </StepWrapper>
      ))}
    </Box>
  );
};
